import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PhoneIcon
} from '@heroicons/react/24/solid';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { getImageUrl } from '@/utils/imagePaths';
import { useCompanyLogo } from '../hooks/useCompanyLogo';

// Informe de diagnóstico público para el cliente (link /d/:token, sin login).
// Mobile-first: el cliente lo abre desde WhatsApp. Tema oscuro por defecto con
// switch claro/oscuro (persistido) y logo acorde al tema (logo / logoDark).

type PointState = 'ok' | 'atencion' | 'urgente' | 'na';
type Theme = 'dark' | 'light';

interface PublicItem {
  pointKey: string | null;
  state: PointState | null;
  category: string;
  subcategory?: string | null;
  description: string;
  severity?: string;
  images: string[];
}

interface PublicPayload {
  diagnosis: {
    diagnosisNumber: string;
    createdAt: string;
    clientName?: string;
    vehiclePlate?: string;
    vehicleBrand?: string;
    vehicleModel?: string;
    vehicleYear?: string | number;
    mileage?: number | null;
    observations?: string | null;
    customerComplaint?: string | null;
    budgetRequested?: boolean;
    hasBudget?: boolean;
  };
  items: PublicItem[];
  sections: Array<{ key: string; label: string; points: Array<{ key: string; label: string }> }>;
}

const THEME_KEY = 'rocam-public-theme';

const STATE_META: Record<PointState, { label: string; icon: React.ReactNode }> = {
  ok: { label: 'Bien', icon: <CheckCircleIcon className="h-5 w-5 text-emerald-500" /> },
  atencion: { label: 'Atención', icon: <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" /> },
  urgente: { label: 'Urgente', icon: <XCircleIcon className="h-5 w-5 text-red-500" /> },
  // 'No aplica' se filtra del informe; la entrada existe solo por tipado/seguridad
  na: { label: 'No aplica', icon: <XCircleIcon className="h-5 w-5 text-gray-400" /> }
};

// Clases del badge según estado y tema (la página maneja su tema propio, sin .dark global)
const badgeCls = (st: PointState, dark: boolean) => {
  if (st === 'ok') return `bg-emerald-500/15 border-emerald-500/30 ${dark ? 'text-emerald-400' : 'text-emerald-600'}`;
  if (st === 'atencion') return `bg-amber-500/15 border-amber-500/30 ${dark ? 'text-amber-400' : 'text-amber-600'}`;
  return `bg-red-500/15 border-red-500/30 ${dark ? 'text-red-400' : 'text-red-600'}`;
};

const stateFromItem = (it: PublicItem): PointState =>
  it.state || (['alta', 'critica'].includes(it.severity || '') ? 'urgente' : 'atencion');

export default function PublicDiagnosis() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicPayload | null>(null);
  const [company, setCompany] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const { getLogoUrl, getLogoDarkUrl } = useCompanyLogo();

  const [theme, setTheme] = useState<Theme>(() =>
    (localStorage.getItem(THEME_KEY) as Theme) === 'light' ? 'light' : 'dark'
  );
  const dark = theme === 'dark';
  const toggleTheme = () => {
    const next: Theme = dark ? 'light' : 'dark';
    setTheme(next);
    try { localStorage.setItem(THEME_KEY, next); } catch { /* modo incógnito */ }
  };

  // Pedido de presupuesto: 'idle' | 'sending' | 'done'
  const [budgetReq, setBudgetReq] = useState<'idle' | 'sending' | 'done'>('idle');

  const requestBudget = async () => {
    if (budgetReq !== 'idle') return;
    setBudgetReq('sending');
    try {
      await axios.post(`/api/diagnostics/public/${token}/request-budget`);
      setBudgetReq('done');
    } catch {
      setBudgetReq('idle');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [diagRes, compRes] = await Promise.all([
          axios.get(`/api/diagnostics/public/${token}`),
          axios.get('/api/settings/company/public').catch(() => ({ data: null }))
        ]);
        setData(diagRes.data);
        setCompany(compRes.data);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'No se pudo cargar el informe');
      }
    })();
  }, [token]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const byKey: Record<string, PublicItem> = {};
    const extras: PublicItem[] = [];
    data.items.forEach(it => { if (it.pointKey) byKey[it.pointKey] = it; else extras.push(it); });
    const sections = data.sections
      .map(sec => ({
        label: sec.label,
        rows: sec.points
          .filter(p => byKey[p.key] && stateFromItem(byKey[p.key]) !== 'na')
          .map(p => {
            const it = byKey[p.key];
            return { label: p.label, state: stateFromItem(it), note: it.description !== p.label ? it.description : '', images: it.images || [] };
          })
      }))
      .filter(sec => sec.rows.length > 0);
    if (extras.length) {
      sections.push({
        label: 'Otros hallazgos',
        rows: extras.map(it => ({ label: it.description, state: stateFromItem(it), note: '', images: it.images || [] }))
      });
    }
    return sections;
  }, [data]);

  const counts = useMemo(() => {
    const all = grouped.flatMap(s => s.rows);
    return {
      ok: all.filter(r => r.state === 'ok').length,
      atencion: all.filter(r => r.state === 'atencion').length,
      urgente: all.filter(r => r.state === 'urgente').length
    };
  }, [grouped]);

  // Paleta por tema (la página vive fuera del layout, maneja su propio tema)
  const cls = {
    page: dark ? 'bg-neutral-950 text-neutral-100' : 'bg-neutral-100 text-neutral-900',
    header: dark ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white',
    card: dark ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white',
    cardBorder: dark ? 'border-neutral-800' : 'border-neutral-200',
    divide: dark ? 'divide-neutral-800/70' : 'divide-neutral-200/80',
    text: dark ? 'text-neutral-100' : 'text-neutral-900',
    sub: dark ? 'text-neutral-300' : 'text-neutral-700',
    muted: dark ? 'text-neutral-400' : 'text-neutral-500',
    faint: dark ? 'text-neutral-500' : 'text-neutral-400',
    footer: dark ? 'text-neutral-600' : 'text-neutral-400',
    toggle: dark ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-neutral-300 text-neutral-600 hover:bg-neutral-100'
  };

  if (error) {
    return (
      <div className={`flex min-h-screen items-center justify-center px-6 text-center ${cls.page}`}>
        <div>
          <p className="text-lg font-semibold">{error}</p>
          <p className={`mt-2 text-sm ${cls.muted}`}>Pedí un link nuevo al taller.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className={`flex min-h-screen items-center justify-center ${cls.page} ${cls.muted}`}>Cargando informe...</div>;
  }

  const d = data.diagnosis;
  const vehicle = [d.vehiclePlate, [d.vehicleBrand, d.vehicleModel].filter(Boolean).join(' '), d.vehicleYear]
    .filter(Boolean).join(' · ');
  const fecha = d.createdAt ? `${d.createdAt.slice(8, 10)}/${d.createdAt.slice(5, 7)}/${d.createdAt.slice(0, 4)}` : '';
  const logoUrl = dark ? getLogoDarkUrl() : getLogoUrl();
  const waDigits = String(company?.whatsapp || company?.phone || '').replace(/[^\d]/g, '');
  const waLink = waDigits ? `https://wa.me/${waDigits.startsWith('549') ? waDigits : '549' + waDigits}` : null;

  return (
    <div className={`min-h-screen pb-16 ${cls.page}`}>
      {/* Header con marca + switch de tema */}
      <div className={`border-b ${cls.header}`}>
        <div className="mx-auto flex w-full max-w-2xl items-center justify-between gap-3 px-4 py-4">
          {logoUrl
            ? <img src={logoUrl} alt={company?.name || 'Logo'} onError={e => { (e.currentTarget as HTMLImageElement).src = '/rocam-new-logo-2025.png'; }} className="h-9 max-w-[160px] object-contain" />
            : <span className="text-lg font-bold">{company?.name || 'ROCAM'}</span>}
          <div className="flex items-center gap-3">
            <span className={`text-xs ${cls.muted}`}>{fecha}</span>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={dark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
              className={`flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${cls.toggle}`}
            >
              {dark ? <SunIcon className="h-4.5 w-4.5 h-[18px] w-[18px]" /> : <MoonIcon className="h-[18px] w-[18px]" />}
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-2xl px-4">
        {/* Título + vehículo */}
        <div className="py-5">
          <h1 className="text-xl font-bold">Informe de inspección</h1>
          <p className={`mt-1 text-sm ${cls.sub}`}>{d.clientName}</p>
          {vehicle && <p className={`text-sm ${cls.muted}`}>{vehicle}{d.mileage ? ` · ${Number(d.mileage).toLocaleString('es-AR')} km` : ''}</p>}
          <p className={`mt-1 text-xs ${cls.faint}`}>{d.diagnosisNumber}</p>
        </div>

        {d.customerComplaint && (
          <div className={`mb-4 rounded-xl border px-4 py-3 ${cls.card}`}>
            <p className={`text-xs font-semibold uppercase tracking-wide ${cls.faint}`}>Tu consulta</p>
            <p className={`mt-1 text-sm ${cls.sub}`}>{d.customerComplaint}</p>
          </div>
        )}

        {/* Resumen semáforo */}
        <div className="grid grid-cols-3 gap-2">
          {(['ok', 'atencion', 'urgente'] as ('ok' | 'atencion' | 'urgente')[]).map(st => (
            <div key={st} className={`rounded-xl border px-3 py-3 text-center ${badgeCls(st, dark)}`}>
              <div className="flex items-center justify-center gap-1.5">
                {STATE_META[st].icon}
                <span className="text-xl font-bold">{counts[st]}</span>
              </div>
              <p className="mt-0.5 text-xs font-medium">{STATE_META[st].label}</p>
            </div>
          ))}
        </div>

        {(counts.urgente > 0 || counts.atencion > 0) && (
          <p className={`mt-3 rounded-lg border px-3 py-2 text-sm ${cls.card} ${cls.sub}`}>
            {counts.urgente > 0
              ? 'Encontramos puntos que conviene resolver cuanto antes por tu seguridad.'
              : 'Encontramos puntos para tener en cuenta en tu próxima visita.'}
          </p>
        )}

        {/* Detalle por sección */}
        <div className="mt-5 space-y-4">
          {grouped.map(sec => (
            <div key={sec.label} className={`overflow-hidden rounded-xl border ${cls.card}`}>
              <div className={`border-b px-4 py-2.5 text-sm font-semibold ${cls.cardBorder} ${cls.sub}`}>{sec.label}</div>
              <div className={`divide-y ${cls.divide}`}>
                {sec.rows.map((row, i) => (
                  <div key={i} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${cls.text}`}>{row.label}</p>
                        {row.note && <p className={`mt-0.5 text-sm ${cls.muted}`}>{row.note}</p>}
                      </div>
                      <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${badgeCls(row.state, dark)}`}>
                        {STATE_META[row.state].icon}
                        {STATE_META[row.state].label}
                      </span>
                    </div>
                    {row.images.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {row.images.map(img => (
                          <button key={img} type="button" onClick={() => setLightbox(getImageUrl(img))}>
                            <img src={getImageUrl(img)} alt="foto" className={`h-20 w-20 rounded-lg border object-cover ${cls.cardBorder}`} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {d.observations && (
          <div className={`mt-4 rounded-xl border px-4 py-3 ${cls.card}`}>
            <p className={`text-sm font-semibold ${cls.sub}`}>Observaciones</p>
            <p className={`mt-1 whitespace-pre-wrap text-sm ${cls.muted}`}>{d.observations}</p>
          </div>
        )}

        {/* CTA: pedir presupuesto (avisa al taller) + WhatsApp */}
        {(counts.atencion + counts.urgente > 0) && !d.hasBudget && (
          (budgetReq === 'done' || d.budgetRequested) ? (
            <div className="mt-6 rounded-xl border border-emerald-600/40 bg-emerald-600/10 px-4 py-3.5 text-center">
              <p className={`font-semibold ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>Recibimos tu pedido de presupuesto</p>
              <p className={`mt-0.5 text-sm ${cls.muted}`}>En breve te contactamos con la cotización.</p>
            </div>
          ) : (
            <button
              type="button"
              onClick={requestBudget}
              disabled={budgetReq === 'sending'}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3.5 text-center font-semibold text-white transition-colors hover:bg-emerald-500 disabled:opacity-60"
            >
              {budgetReq === 'sending' ? 'Enviando...' : 'Solicitar presupuesto'}
            </button>
          )
        )}
        {waLink && (
          <a
            href={`${waLink}?text=${encodeURIComponent(`Hola! Vi el informe ${d.diagnosisNumber} de mi vehículo${d.vehiclePlate ? ` (${d.vehiclePlate})` : ''} y quiero coordinar los trabajos.`)}`}
            target="_blank"
            rel="noreferrer"
            className={`mt-3 flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-center font-semibold transition-colors ${dark ? 'border-neutral-700 text-neutral-200 hover:bg-neutral-900' : 'border-neutral-300 text-neutral-700 hover:bg-white'}`}
          >
            <PhoneIcon className="h-5 w-5" /> Coordinar por WhatsApp
          </a>
        )}

        <p className={`mt-6 text-center text-xs ${cls.footer}`}>
          {company?.name || 'ROCAM'}{company?.address ? ` · ${company.address}` : ''}
        </p>
      </div>

      {/* Lightbox simple */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="foto" className="max-h-full max-w-full rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}
